import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { costSheetAPI, itemsAPI } from '../api';
const authHeaders = () => { try { const u = JSON.parse(localStorage.getItem('npd_user')); return u?.token ? { 'Authorization': 'Bearer ' + u.token } : {}; } catch { return {}; } };

function TemplateSearchModal({ type, onSelect, onClose }) {
  const [templates, setTemplates] = React.useState([]);
  React.useEffect(() => {
    const base = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';
    fetch(base + '/costing-templates?type=' + type, { headers: authHeaders() }).then(r=>r.json()).then(setTemplates).catch(()=>{});
  }, [type]);
  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">Load {type === 'packing' ? 'Packing' : 'Operation'} Template</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {templates.length === 0 ? (
          <div className="empty-state"><p>No {type} templates found. Create them in Settings → Cost Templates.</p></div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {templates.map(t => (
              <div key={t.id} onClick={() => onSelect(t)} style={{ padding: '12px 16px', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.background='var(--bg-hover)'}
                onMouseLeave={e => e.currentTarget.style.background='white'}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>{t.name}</div>
                  {t.description && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.description}</div>}
                </div>
                <span className="btn btn-primary btn-sm">Use</span>
              </div>
            ))}
          </div>
        )}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function CostSheet({ product }) {
  const [data, setData] = useState(null);
  const [editCost, setEditCost] = useState(null);
  const [editOp, setEditOp] = useState(null); // { id, description, amount } // { kind:'rm'|'pack'|'op', id, item_code, recipe_id, value, updateMaster, row }

  const reloadSheet = () => costSheetAPI.get(product.id).then(r => setData(r.data));
  const saveEditOp = async () => {
    if (!editOp) return;
    try {
      const m = await import('axios');
      const API2 = m.default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
      API2.interceptors.request.use(cfg => { try { const u = JSON.parse(localStorage.getItem('npd_user')); if (u?.token) cfg.headers.Authorization = 'Bearer ' + u.token; } catch {} return cfg; });
      await API2.put('/costsheet/operations/' + editOp.id, { description: editOp.description, amount: parseFloat(editOp.amount) || 0 });
      setEditOp(null);
      reloadSheet();
    } catch (e) { alert(e.response?.data?.error || 'Save failed'); }
  };
  const saveRmCost = async () => {
    if (!editCost) return;
    try {
      const m = await import('axios');
      const API2 = m.default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
      API2.interceptors.request.use(cfg => { try { const u = JSON.parse(localStorage.getItem('npd_user')); if (u?.token) cfg.headers.Authorization = 'Bearer ' + u.token; } catch {} return cfg; });
      await API2.put('/recipes/' + editCost.recipe_id + '/ingredient-cost', { item_code: editCost.item_code, unit_cost: parseFloat(editCost.value) || 0, update_master: editCost.updateMaster });
      setEditCost(null);
      reloadSheet();
    } catch (e) { alert(e.response?.data?.error || 'Save failed'); }
  };

  const [batchSize, setBatchSize] = useState(100);
  const [itemMaster, setItemMaster] = useState([]);
  const [showAddPacking, setShowAddPacking] = useState(false);
  const [packingSearch, setPackingSearch] = useState('');
  const [showPackingDropdown, setShowPackingDropdown] = useState(false);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('npd_user')); } catch { return null; } })();
  const isApprover = currentUser?.role === 'approver';
  const latestVersion = versions.length > 0 ? versions[0] : null;
  const isApproved = latestVersion?.approval_status === 'approved';
  const isPending = latestVersion?.approval_status === 'pending';
  const [creatingVersion, setCreatingVersion] = useState(false);
  const [showPackingTemplate, setShowPackingTemplate] = useState(false);
  const [showOpTemplate, setShowOpTemplate] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showAddOp, setShowAddOp] = useState(false);
  const [packingForm, setPackingForm] = useState({ item_code: '', item_name: '', quantity: '', unit: '', unit_cost: '' });
  const [opForm, setOpForm] = useState({ description: '', amount: '' });

  const load = useCallback(() => {
    costSheetAPI.get(product.id).then(r => { setData(r.data); setBatchSize(r.data.batchSize || 100); });
    import('axios').then(m => m.default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' }).get('/items?category=PM').then(r => setItemMaster(r.data)));
    import('axios').then(m => m.default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' })
      .get(`/products/${product.id}/costsheet/versions-list`)
      .then(r => setVersions(r.data)));
  }, [product.id]);

  useEffect(() => {
    load();
    itemsAPI.getAll().then(r => setItemMaster(r.data));
  }, [product.id]);

  const handleItemSelect = (code) => {
    const item = itemMaster.find(i => i.item_code === code);
    if (item) setPackingForm({ item_code: item.item_code, item_name: item.item_name, quantity: '', unit: item.unit || '', unit_cost: item.unit_cost || '' });
  };

  const submitCostSheetForApproval = async () => {
    if (!latestVersion) return toast.error('Create a version first');
    try {
      const API = (await import('axios')).default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
      await API.put(`/costsheet/versions/${latestVersion.id}/submit`);
      toast.success('Submitted for approval!');
      load();
    } catch(e) { toast.error('Failed to submit'); }
  };

  const approveCostSheet = async () => {
    if (!latestVersion) return;
    try {
      const API = (await import('axios')).default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
      await API.put(`/costsheet/versions/${latestVersion.id}/approve`, { approved_by: currentUser?.name || currentUser?.username });
      toast.success('Cost sheet approved!');
      load();
    } catch(e) { toast.error('Failed to approve'); }
  };

  const unlockCostSheet = async () => {
    if (!latestVersion) return;
    try {
      const API = (await import('axios')).default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
      await API.put(`/costsheet/versions/${latestVersion.id}/unlock`);
      toast.success('Cost sheet unlocked');
      load();
    } catch(e) { toast.error('Failed to unlock'); }
  };

  const createVersion = async () => {
    setCreatingVersion(true);
    try {
      const API = (await import('axios')).default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
      await API.post(`/products/${product.id}/costsheet/new-version`, {});
      toast.success('New cost sheet version created');
      load();
    } catch(e) { toast.error('Failed to create version'); }
    setCreatingVersion(false);
  };

  const addPacking = async () => {
    if (!packingForm.item_name) return toast.error('Item name required');
    await costSheetAPI.addPacking(product.id, packingForm);
    toast.success('Packing material added');
    setShowAddPacking(false);
    setPackingForm({ item_code: '', item_name: '', quantity: '', unit: '', unit_cost: '' });
    load();
  };

  const deletePacking = async (id) => {
    await costSheetAPI.deletePacking(id);
    load();
  };

  const addOperation = async () => {
    if (!opForm.description) return toast.error('Description required');
    await costSheetAPI.addOperation(product.id, opForm);
    toast.success('Operation cost added');
    setShowAddOp(false);
    setOpForm({ description: '', amount: '' });
    load();
  };

  const deleteOperation = async (id) => {
    await costSheetAPI.deleteOperation(id);
    load();
  };

  const printPDFForVersion = (v) => {
    if (!data) return;
    const savedDocNumber = data.docNumber;
    const savedVersionNumber = data.versionNumber;
    data.docNumber = v.doc_number;
    data.versionNumber = v.version_number;
    printPDF();
    data.docNumber = savedDocNumber;
    data.versionNumber = savedVersionNumber;
  };

  const printPDF = () => {
    if (!data) return;
    const win = window.open('', '_blank');
    const now = new Date();
    const today = now.toLocaleDateString('en-AE', { day: '2-digit', month: 'short', year: 'numeric' });
    const timeStr = now.toLocaleTimeString('en-AE', { hour: '2-digit', minute: '2-digit' });
    const printedAt = `${today} ${timeStr}`;
    const userName = data.rawMaterials.length > 0 && data.confirmedBy ? data.confirmedBy : (() => { try { const u = JSON.parse(localStorage.getItem('npd_user')); return u?.name || u?.username || 'R&D Team'; } catch { return 'R&D Team'; } })();

    const rmRows = data.rawMaterials.map((i, idx) => `
      <tr style="${idx%2===1?'background:#fdf9fc;':''}">
        <td style="font-family:'Courier New',monospace;font-size:9px;color:#7B2D6B;font-weight:600;padding:5px 8px;border-bottom:0.5px solid #f5edf3;">${i.item_code}</td>
        <td style="font-size:10px;font-weight:400;padding:5px 8px;border-bottom:0.5px solid #f5edf3;text-align:center;font-family:'Inter',Arial,sans-serif;letter-spacing:0.01em;">${i.item_name||'—'}</td>
        <td style="text-align:center;font-size:10px;font-family:'Inter',Arial,sans-serif;padding:5px 8px;border-bottom:0.5px solid #f5edf3;">${i.quantity} ${i.unit}</td>
        <td style="text-align:right;padding:5px 8px;border-bottom:0.5px solid #f5edf3;">AED ${(i.unit_cost||0).toLocaleString('en-AE',{minimumFractionDigits:2})}</td>
        <td style="text-align:right;font-weight:600;color:#7B2D6B;padding:5px 8px;border-bottom:0.5px solid #f5edf3;">AED ${(i.total_cost||0).toLocaleString('en-AE',{minimumFractionDigits:2})}</td>
      </tr>`).join('');

    const pkgRows = data.packing.map((i, idx) => `
      <tr style="${idx%2===1?'background:#fffaf8;':''}">
        <td style="font-family:'Courier New',monospace;font-size:9px;color:#7B2D6B;font-weight:600;padding:5px 8px;border-bottom:0.5px solid #f8ede8;">${i.item_code||'—'}</td>
        <td style="font-size:10px;font-weight:400;padding:5px 8px;border-bottom:0.5px solid #f8ede8;text-align:center;font-family:'Inter',Arial,sans-serif;">${i.item_name}</td>
        <td style="text-align:center;font-size:10px;font-family:'Inter',Arial,sans-serif;padding:5px 8px;border-bottom:0.5px solid #f8ede8;">${i.quantity} ${i.unit}</td>
        <td style="text-align:right;padding:5px 8px;border-bottom:0.5px solid #f8ede8;">AED ${(i.unit_cost||0).toLocaleString('en-AE',{minimumFractionDigits:2})}</td>
        <td style="text-align:right;font-weight:600;color:#E8541A;padding:5px 8px;border-bottom:0.5px solid #f8ede8;">AED ${(i.total_cost||0).toLocaleString('en-AE',{minimumFractionDigits:2})}</td>
      </tr>`).join('');

    const opRows = data.operations.map((o, idx) => `
      <tr style="${idx%2===1?'background:#f9f9f9;':''}">
        <td style="padding:5px 8px;border-bottom:0.5px solid #f0f0f0;">${o.description}</td>
        <td style="text-align:right;font-weight:600;color:#555;padding:5px 8px;border-bottom:0.5px solid #f0f0f0;">AED ${(o.amount||0).toLocaleString('en-AE',{minimumFractionDigits:2})}</td>
      </tr>`).join('');

    const fmtAED = (n) => `AED ${(n||0).toLocaleString('en-AE',{minimumFractionDigits:2,maximumFractionDigits:2})}`;

    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8">
    <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
    <title>Product Costing Sheet - ${product.name} - ${product.project_code||product.project_name||''} - v${data.versionNumber||1}</title>
    <style>
      *{box-sizing:border-box;margin:0;padding:0;}
      body{font-family:'Inter',Arial,sans-serif;background:#fff;padding:28px;color:#1a1a1a;font-size:11px;}
      .top-bar{height:4px;background:linear-gradient(90deg,#7B2D6B 0%,#a03880 55%,#E8541A 100%);border-radius:2px 2px 0 0;}
      .bottom-bar{height:4px;background:linear-gradient(90deg,#7B2D6B 0%,#a03880 55%,#E8541A 100%);border-radius:0 0 2px 2px;margin-top:14px;}
      table{width:100%;border-collapse:collapse;}
      th{padding:5px 8px;text-align:left;font-size:7px;letter-spacing:0.1em;text-transform:uppercase;color:#c0a0b8;font-weight:600;border-bottom:1px solid #e4d4e0;}
      .sec-head{display:flex;align-items:center;gap:8px;margin-bottom:8px;}
      .sec-bar{width:3px;border-radius:2px;flex-shrink:0;}
      .sec-title{font-size:7px;letter-spacing:0.2em;text-transform:uppercase;font-weight:700;}
      .sec-line{flex:1;height:1px;opacity:0.18;}
      .subtotal td{border-top:1px solid #e4d4e0;font-weight:700;}
      .summary{display:grid;grid-template-columns:1fr 1fr 1fr;border:1px solid #e4d4e0;border-radius:4px;overflow:hidden;margin-top:16px;}
      .sc{padding:10px 14px;border-right:1px solid #e4d4e0;}
      .sc:last-child{border-right:none;background:#7B2D6B;}
      .sl{font-size:7px;letter-spacing:0.14em;text-transform:uppercase;color:#c0a0b8;margin-bottom:3px;}
      .sc:last-child .sl{color:rgba(255,255,255,0.6);}
      .sv{font-size:14px;font-weight:700;color:#7B2D6B;}
      .sc:last-child .sv{color:#fff;font-size:15px;}
      .footer-row{display:grid;grid-template-columns:1fr 1fr 1fr;border:1px solid #e4d4e0;border-radius:3px;overflow:hidden;margin-top:14px;}
      .fc{padding:8px 12px;border-right:1px solid #e4d4e0;}
      .fc:last-child{border-right:none;background:#7B2D6B;}
      .fl{font-size:7px;letter-spacing:0.14em;text-transform:uppercase;color:#c0a0b8;margin-bottom:3px;}
      .fc:last-child .fl{color:rgba(255,255,255,0.55);}
      .fv{font-size:10px;font-weight:600;color:#333;}
      .fc:last-child .fv{color:#fff;}
      @media print{body{padding:16px;} @page{margin:0; size:A4;} body{padding:20px 28px;}}
    </style></head><body>
    <div class="top-bar"></div>

    <div style="padding:12px 0 10px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #f0e8ed;margin-bottom:12px;">
      <div>
        <div style="font-family:'Playfair Display',serif;font-size:13px;font-weight:700;color:#7B2D6B;">Kallada Food Industries LLC</div>
        <div style="font-size:8px;color:#c0a0b8;margin-top:1px;">Dubai, United Arab Emirates</div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:26px;font-weight:800;color:#7B2D6B;letter-spacing:-0.02em;position:relative;display:inline-block;">BGH<span style="position:absolute;top:1px;right:-9px;width:7px;height:7px;background:#E8541A;border-radius:1.5px;display:inline-block;"></span></div>
        <div style="font-size:6px;letter-spacing:0.16em;text-transform:uppercase;color:#E8541A;font-weight:600;margin-top:3px;">Bettergrow Holding</div>
      </div>
    </div>

    <div style="text-align:center;padding:10px 0 12px;border-bottom:1px solid #f0e8ed;margin-bottom:14px;">
      <div style="font-size:18px;font-weight:800;color:#1a1a1a;letter-spacing:0.12em;text-transform:uppercase;">Product Cost Sheet</div>
    </div>

    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
      <div style="font-family:monospace;font-size:10px;font-weight:700;color:#7B2D6B;">Doc No: ${data.docNumber || 'DRAFT'}</div>
      <div style="font-family:monospace;font-size:10px;font-weight:700;color:#555;">Version: ${data.versionNumber ? 'v'+data.versionNumber : 'DRAFT'}</div>
    </div>
    <div style="background:#f9f3f8;border:1px solid #ede0ea;border-radius:3px;padding:8px 14px;margin-bottom:16px;">
      <div style="font-size:14px;font-weight:700;color:#7B2D6B;margin-bottom:8px;text-align:center;letter-spacing:0.03em;">${product.name}</div>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;">
        <div style="text-align:center;"><div style="font-size:7px;color:#c0a0b8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:2px;">Product Code</div><div style="font-family:monospace;font-size:11px;font-weight:700;color:#555;">${product.product_code}</div></div>
        <div style="text-align:center;"><div style="font-size:7px;color:#c0a0b8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:2px;">Department</div><div style="font-size:11px;font-weight:700;color:#333;">${product.department||'—'}</div></div>
        <div style="text-align:center;"><div style="font-size:7px;color:#c0a0b8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:2px;">Date</div><div style="font-size:11px;font-weight:700;color:#333;">${today}</div></div>
      </div>
    </div>

    <div style="margin-bottom:16px;">
      <div class="sec-head" style="color:#7B2D6B;">
        <div class="sec-bar" style="background:#7B2D6B;height:14px;"></div>
        <div class="sec-title" style="color:#7B2D6B;">1. Raw Materials (from Recipe) {data?.sfgInputPct > 0 && data.sfgInputPct !== 100 && <span style={{ fontSize: 11, color: '#E8541A', fontWeight: 700, marginLeft: 8 }}>SFG input {data.sfgInputPct}%{data.sfgInputPct > 100 ? ' — process loss' : ''}</span>}</div>
        <div class="sec-line" style="background:#7B2D6B;"></div>
        <div style="font-size:10px;font-weight:700;color:#7B2D6B;">${fmtAED(data.rawTotal)}</div>
      </div>
      ${data.rawMaterials.length === 0 ? '<div style="padding:10px;color:#ccc;font-style:italic;font-size:10px;border:1px solid #f0e8ed;border-radius:3px;">No recipe confirmed yet.</div>' : `
      <table>
        <thead><tr><th style="width:90px;">Item Code</th><th>Description</th><th style="width:80px;text-align:center;">Qty & Unit</th><th style="width:100px;text-align:right;">Unit Cost</th><th style="width:110px;text-align:right;">Total (AED)</th></tr></thead>
        <tbody>
          ${rmRows}
          <tr class="subtotal"><td colspan="4" style="text-align:right;color:#7B2D6B;font-size:9px;text-transform:uppercase;letter-spacing:0.06em;padding:6px 8px;background:#f9f3f8;">Raw Materials Total</td><td style="text-align:right;color:#7B2D6B;font-size:11px;padding:6px 8px;background:#f9f3f8;">${fmtAED(data.rawTotal)}</td></tr>
        </tbody>
      </table>`}
    </div>

    <div style="margin-bottom:16px;">
      <div class="sec-head" style="color:#E8541A;">
        <div class="sec-bar" style="background:#E8541A;height:14px;"></div>
        <div class="sec-title" style="color:#E8541A;">2. Packing Materials</div>
        <div class="sec-line" style="background:#E8541A;"></div>
        <div style="font-size:10px;font-weight:700;color:#E8541A;">${fmtAED(data.packingTotal)}</div>
      </div>
      ${data.packing.length === 0 ? '<div style="padding:10px;color:#ccc;font-style:italic;font-size:10px;border:1px solid #f0e8ed;border-radius:3px;">No packing materials added.</div>' : `
      <table>
        <thead><tr><th style="width:90px;">Item Code</th><th>Description</th><th style="width:80px;text-align:center;">Qty & Unit</th><th style="width:100px;text-align:right;">Unit Cost</th><th style="width:110px;text-align:right;">Total (AED)</th></tr></thead>
        <tbody>
          ${pkgRows}
          <tr class="subtotal"><td colspan="4" style="text-align:right;color:#E8541A;font-size:9px;text-transform:uppercase;letter-spacing:0.06em;padding:6px 8px;background:#fff3ee;">Packing Total</td><td style="text-align:right;color:#E8541A;font-size:11px;padding:6px 8px;background:#fff3ee;">${fmtAED(data.packingTotal)}</td></tr>
        </tbody>
      </table>`}
    </div>

    <div style="margin-bottom:16px;">
      <div class="sec-head" style="color:#555;">
        <div class="sec-bar" style="background:#555;height:14px;"></div>
        <div class="sec-title" style="color:#555;">3. Operation Costs</div>
        <div class="sec-line" style="background:#555;"></div>
        <div style="font-size:10px;font-weight:700;color:#555;">${fmtAED(data.opsTotal)}</div>
      </div>
      ${data.operations.length === 0 ? '<div style="padding:10px;color:#ccc;font-style:italic;font-size:10px;border:1px solid #e5e5e5;border-radius:3px;">No operation costs added.</div>' : `
      <table>
        <thead><tr><th>Description</th><th style="width:130px;text-align:right;">Amount (AED)</th></tr></thead>
        <tbody>
          ${opRows}
          <tr class="subtotal"><td style="text-align:right;color:#555;font-size:9px;text-transform:uppercase;letter-spacing:0.06em;padding:6px 8px;background:#f7f7f7;">Operations Total</td><td style="text-align:right;color:#555;font-size:11px;padding:6px 8px;background:#f7f7f7;">${fmtAED(data.opsTotal)}</td></tr>
        </tbody>
      </table>`}
    </div>

    <div class="summary">
      <div class="sc"><div class="sl">Raw Materials</div><div class="sv">${fmtAED(data.rawTotal)}</div></div>
      <div class="sc"><div class="sl">Packing Materials</div><div class="sv" style="color:#E8541A;">${fmtAED(data.packingTotal)}</div></div>
      <div class="sc"><div class="sl">Total Product Cost</div><div class="sv">${fmtAED(data.grandTotal)}</div></div>
    </div>

    <div class="footer-row">
      <div class="fc"><div class="fl">Prepared By</div><div class="fv">${userName}</div></div>
      <div class="fc"><div class="fl">Approved By</div><div class="fv" style="color:${latestVersion?.approved_by ? '#2e7d32' : '#333'}">${latestVersion?.approved_by || '—'}</div></div>
      <div class="fc"><div class="fl">Product Status</div><div class="fv">${({'trial':'Trial','recipe_confirmed':'Recipe Confirmed','commercial_trial':'Commercial Trial','launched':'Launched'})[product.status] || product.status}</div></div>
    </div>

    <div style="margin-top:12px;text-align:center;">
      <span style="font-size:7px;color:#d0bfca;letter-spacing:0.07em;text-transform:uppercase;">Kallada Food Industries LLC · Confidential R&D Document · Printed: ${printedAt}</span>
    </div>
    <div class="bottom-bar"></div>
    </body></html>`);
    win.document.close();
    win.focus();
    // PDF preview - user prints manually
  };

  const [showSfgOpsInfo, setShowSfgOpsInfo] = useState(false);
  const [editPackingItem, setEditPackingItem] = useState(null);
  const [editPackingForm, setEditPackingForm] = useState({});
  const [editOpItem, setEditOpItem] = useState(null);
  const [editOpForm, setEditOpForm] = useState({});

  if (!data) return <div className="empty-state"><p>Loading...</p></div>;

  const fmt = (n) => `AED ${(n||0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;


  const saveAsTemplate = async (type) => {
    const name = window.prompt('Template name:');
    if (!name) return;
    const base = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';
    try {
      let items = [];
      if (type === 'packing') {
        items = (data.packing||[]).map(i => ({ item_code: i.item_code, item_name: i.item_name, quantity: i.quantity, unit: i.unit }));
      } else {
        items = (data.operations||[]).map(i => ({ operation_name: i.description||i.operation_name, rate: i.amount||i.rate, unit: i.unit||"job" }));
      }
      await fetch(base + '/costing-templates', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name, type, items })
      });
      alert('Template saved!');
    } catch(e) { alert('Failed: ' + e.message); }
  };

  const saveEditPacking = async () => {
    try {
      await costSheetAPI.updatePacking(editPackingItem.id, editPackingForm);
      setEditPackingItem(null);
      load();
      toast.success('Updated!');
    } catch(e) { toast.error('Failed'); }
  };


  const loadPackingTemplate = async (template) => {
    const base = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';
    const r = await fetch(base + '/costing-templates/' + template.id, { headers: authHeaders() });
    const tpl = await r.json();
    if (tpl.items) {
      for (const item of tpl.items) {
        await costSheetAPI.addPacking(product.id, {
          item_code: item.item_code, item_name: item.item_name,
          quantity: item.quantity, unit: item.unit,
          unit_cost: item.unit_cost || 0,
          total_cost: (item.quantity||0) * (item.unit_cost||0)
        });
      }
      load();
    }
    setShowPackingTemplate(false);
  };

  const loadOpTemplate = async (template) => {
    const base = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';
    const r = await fetch(base + '/costing-templates/' + template.id, { headers: authHeaders() });
    const tpl = await r.json();
    if (tpl.items) {
      for (const item of tpl.items) {
        await costSheetAPI.addOperation(product.id, { description: item.operation_name, amount: item.rate });
      }
      load();
    }
    setShowOpTemplate(false);
  };

  return (
    <div>
      <div className="flex-between mb-6">
        <div className="flex gap-3" style={{ alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Document No.</div>
              <div className="mono" style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>{data?.docNumber || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Version</div>
              <div className="mono" style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>v{data?.versionNumber || 1}</div>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {isApproved ? (
            <>
              <span style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 10px', background:'var(--accent-dim)', borderRadius:20, fontSize:11, color:'var(--accent)' }}>✓ Approved by {latestVersion?.approved_by}</span>
              {isApprover && <button className="btn btn-secondary btn-sm" onClick={unlockCostSheet}>🔓 Unlock</button>}
            </>
          ) : isPending ? (
            <>
              <span style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 10px', background:'var(--amber-dim)', borderRadius:20, fontSize:11, color:'var(--amber)' }}>⏳ Pending Approval</span>
              {isApprover && <button className="btn btn-primary btn-sm" onClick={approveCostSheet}>✓ Approve</button>}
            </>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={submitCostSheetForApproval} disabled={!latestVersion}>Submit for Approval</button>
              <button className="btn btn-secondary" onClick={createVersion} disabled={creatingVersion || isApproved}>
                {creatingVersion ? 'Creating...' : '+ New Version'}
              </button>
            </>
          )}
          <button className="btn btn-primary" onClick={printPDF}>⬇ Download / Print PDF</button>
        </div>
      </div>
      {versions.length > 0 && (
        <div className="card mb-6" style={{ padding: '12px 16px' }}>
          <div className="card-title" style={{ margin: '0 0 8px' }}>Version History</div>
          <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
            {versions.map(v => (
              <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: v.version_number === data?.versionNumber ? 'var(--accent-dim)' : 'var(--bg-elevated)', border: `1px solid ${v.version_number === data?.versionNumber ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 8, fontSize: 11 }}>
                <span className="mono" style={{ color: 'var(--accent)', fontWeight: 700 }}>v{v.version_number}</span>
                <span className="text-muted" style={{ fontSize: 10 }}>{v.doc_number}</span>
                <span className="text-muted" style={{ fontSize: 10 }}>{new Date(v.created_at).toLocaleDateString('en-AE')}</span>
                <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: '2px 8px' }} onClick={() => printPDFForVersion(v)}>⬇ PDF</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div id="cost-sheet-print">
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 800 }}>{product.name}</div>
          <div className="meta" style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>
            <span className="mono">{product.product_code}</span>
            <span> · {product.department}</span>
            <span> · Group: {product.internal_group}</span>
            {product.product_type && <span> · {product.product_type}</span>}
            <span> · Generated: {new Date().toLocaleDateString('en-AE')}</span>
          </div>
        </div>

        {/* Raw Materials */}
        <div className="card mb-6">
          <div className="flex-between mb-4">
            <div className="card-title" style={{ margin: 0 }}>1. Raw Materials (from Recipe)</div>
            <div className="cost-value mono">{fmt(data.rawTotal)}</div>
          </div>
          {data.rawMaterials.length === 0 ? (
            <div className="text-muted" style={{ fontSize: 13 }}>No recipe confirmed yet. Confirm a recipe first.</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Item Code</th><th>Description</th><th>Qty</th><th>Unit</th><th className="text-right">Unit Cost</th><th className="text-right">Total</th></tr></thead>
                <tbody>
                  {data.rawMaterials.map(i => (
                    <tr key={i.id}>
                      <td className="mono">{i.item_code}</td>
                      <td>{i.item_name}</td>
                      <td className="mono">{i.quantity}</td>
                      <td>{i.unit}</td>
                      <td className="text-right mono" style={{ cursor: i.source === 'sfg' ? 'default' : 'pointer' }}
                        title={i.source === 'sfg' ? 'Edit on the SFG product' : 'Click to edit cost'}
                        onClick={() => { if (i.source !== 'sfg') setEditCost({ kind: 'rm', id: i.id, item_code: i.item_code, recipe_id: i.recipe_id, value: i.unit_cost || 0, updateMaster: false, row: i }); }}>
                        {editCost?.kind === 'rm' && editCost.id === i.id ? (
                          <span onClick={e => e.stopPropagation()} style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                            <input autoFocus type="number" step="0.01" value={editCost.value}
                              onChange={e => setEditCost(ec => ({ ...ec, value: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') saveRmCost(); if (e.key === 'Escape') setEditCost(null); }}
                              style={{ width: 90, padding: '4px 6px', border: '1.5px solid var(--accent)', borderRadius: 6, textAlign: 'right', color: '#111', background: '#fff' }} />
                            <label style={{ fontSize: 10, display: 'flex', gap: 4, alignItems: 'center', whiteSpace: 'nowrap' }}>
                              <input type="checkbox" checked={editCost.updateMaster} onChange={e => setEditCost(ec => ({ ...ec, updateMaster: e.target.checked }))} />
                              also update item master
                            </label>
                            <span style={{ display: 'flex', gap: 4 }}>
                              <button className="btn btn-primary btn-sm" style={{ fontSize: 10, padding: '2px 8px' }} onClick={saveRmCost}>💾</button>
                              <button className="btn btn-secondary btn-sm" style={{ fontSize: 10, padding: '2px 8px' }} onClick={() => setEditCost(null)}>✕</button>
                            </span>
                          </span>
                        ) : <>{fmt(i.unit_cost)}{i.source !== 'sfg' && <span style={{ fontSize: 9, marginLeft: 4, color: 'var(--text-muted)' }}>✎</span>}</>}
                      </td>
                      <td className="text-right cost-value">{fmt(i.total_cost)}</td>
                    </tr>
                  ))}
                  <tr style={{ background: 'var(--bg-elevated)', fontWeight: 600 }}>
                    <td colSpan={5} style={{ textAlign: 'right', fontWeight: 700 }}>Raw Materials Total</td>
                    <td className="text-right cost-value">{fmt(data.rawTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Packing Materials */}
        <div className="card mb-6">
          <div className="flex-between mb-4">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}><div className="card-title" style={{margin:0}}>2. Packing Materials</div><div style={{display:"flex",gap:6}}><button className="btn btn-secondary btn-sm" onClick={()=>setShowPackingTemplate(true)}>Load Template</button><button className="btn btn-secondary btn-sm" onClick={()=>saveAsTemplate("packing")}>Save as Template</button></div></div>
            <div className="flex gap-2" style={{ alignItems: 'center' }}>
              <div className="cost-value mono">{fmt(data.packingTotal)}</div>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowAddPacking(true)}>+ Add</button>
            </div>
          </div>
          {data.packing.length === 0 ? (
            <div className="text-muted" style={{ fontSize: 13 }}>No packing materials added yet.</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Item Code</th><th>Description</th><th>Qty</th><th>Unit</th><th className="text-right">Unit Cost</th><th className="text-right">Total</th><th></th></tr></thead>
                <tbody>
                  {data.packing.map(i => (
                    <tr key={i.id}>
                      {editPackingItem?.id === i.id ? (
                        <>
                          <td className="mono">{i.item_code || '—'}</td>
                          <td>{i.item_name}</td>
                          <td><input type="number" value={editPackingForm.quantity} onChange={e => setEditPackingForm({...editPackingForm, quantity: e.target.value})} style={{ width: 70, fontSize: 12, padding: '2px 4px' }} /></td>
                          <td><input value={editPackingForm.unit} onChange={e => setEditPackingForm({...editPackingForm, unit: e.target.value})} style={{ width: 50, fontSize: 12, padding: '2px 4px' }} /></td>
                          <td><input type="number" value={editPackingForm.unit_cost} onChange={e => setEditPackingForm({...editPackingForm, unit_cost: e.target.value})} style={{ width: 80, fontSize: 12, padding: '2px 4px' }} /></td>
                          <td className="text-right cost-value">{fmt((parseFloat(editPackingForm.quantity)||0)*(parseFloat(editPackingForm.unit_cost)||0))}</td>
                          <td style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-primary btn-sm" onClick={saveEditPacking}>✓</button>
                            <button className="btn btn-secondary btn-sm" onClick={() => setEditPackingItem(null)}>✕</button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="mono">{i.item_code || '—'}</td>
                          <td>{i.item_name}</td>
                          <td className="mono">{i.quantity}</td>
                          <td>{i.unit}</td>
                          <td className="text-right mono">{fmt(i.unit_cost)}</td>
                          <td className="text-right cost-value">{fmt(i.total_cost)}</td>
                          <td style={{ display: 'flex', gap: 4 }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => { setEditPackingItem(i); setEditPackingForm({ item_name: i.item_name, item_code: i.item_code, quantity: i.quantity, unit: i.unit, unit_cost: i.unit_cost }); }}>✎</button>
                            <button className="btn btn-danger btn-sm" onClick={() => deletePacking(i.id)}>×</button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                  <tr style={{ background: 'var(--bg-elevated)', fontWeight: 600 }}>
                    <td colSpan={6} style={{ textAlign: 'right', fontWeight: 700 }}>Packing Materials Total</td>
                    <td className="text-right cost-value">{fmt(data.packingTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Operation Costs */}
        <div className="card mb-6">
          <div className="flex-between mb-4">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div className="card-title" style={{margin:0}}>3. Operation Costs</div>
                {data.operations.some(o => String(o.id).startsWith('sfg-op-')) && (
                  <button onClick={() => setShowSfgOpsInfo(true)} style={{ background: 'var(--accent)', color: 'white', border: 'none', borderRadius: '50%', width: 20, height: 20, fontSize: 11, fontWeight: 700, cursor: 'pointer', lineHeight: '20px', textAlign: 'center' }}>?</button>
                )}
              </div><div style={{display:"flex",gap:6}}><button className="btn btn-secondary btn-sm" onClick={()=>setShowOpTemplate(true)}>Load Template</button><button className="btn btn-secondary btn-sm" onClick={()=>saveAsTemplate("operation")}>Save as Template</button></div></div>
            <div className="flex gap-2" style={{ alignItems: 'center' }}>
              <div className="cost-value mono">{fmt(data.opsTotal)}</div>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowAddOp(true)}>+ Add</button>
            </div>
          </div>
          {data.operations.length === 0 ? (
            <div className="text-muted" style={{ fontSize: 13 }}>No operation costs added yet.</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Description</th><th className="text-right">Amount</th><th></th></tr></thead>
                <tbody>
                  {data.operations.map(o => {
                    const isSfg = String(o.id).startsWith('sfg-op-');
                    const editing = editOp?.id === o.id;
                    return (
                    <tr key={o.id}>
                      <td>{editing ? <input value={editOp.description} onChange={e => setEditOp(f => ({ ...f, description: e.target.value }))} style={{ width: '95%', fontSize: 12, padding: '3px 6px', color: '#111', background: '#fff', border: '1px solid var(--accent)', borderRadius: 4 }} /> : o.description}</td>
                      <td className="text-right cost-value">{editing ? (
                        <input type="number" step="0.01" value={editOp.amount} autoFocus
                          onChange={e => setEditOp(f => ({ ...f, amount: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') saveEditOp(); if (e.key === 'Escape') setEditOp(null); }}
                          style={{ width: 90, fontSize: 12, padding: '3px 6px', textAlign: 'right', color: '#111', background: '#fff', border: '1px solid var(--accent)', borderRadius: 4 }} />
                      ) : fmt(o.amount)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {isSfg ? <span style={{ fontSize: 9, color: 'var(--text-muted)' }} title="Edit on the SFG product">via SFG</span> : editing ? (
                          <>
                            <button className="btn btn-primary btn-sm" onClick={saveEditOp}>✓</button>
                            <button className="btn btn-secondary btn-sm" style={{ marginLeft: 4 }} onClick={() => setEditOp(null)}>✕</button>
                          </>
                        ) : (
                          <>
                            <button className="btn btn-secondary btn-sm" onClick={() => setEditOp({ id: o.id, description: o.description, amount: o.amount })}>✎</button>
                            <button className="btn btn-danger btn-sm" style={{ marginLeft: 4 }} onClick={() => deleteOperation(o.id)}>×</button>
                          </>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                  <tr style={{ background: 'var(--bg-elevated)', fontWeight: 600 }}>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>Operation Costs Total</td>
                    <td className="text-right cost-value">{fmt(data.opsTotal)}</td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Grand Total */}
        <div style={{ background: 'var(--accent-dim)', border: '1px solid var(--accent)', borderRadius: 10, padding: '16px 20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className="flex-between"><span className="text-secondary">Raw Materials</span><span className="mono">{fmt(data.rawTotal)}</span></div>
            <div className="flex-between"><span className="text-secondary">Packing Materials</span><span className="mono">{fmt(data.packingTotal)}</span></div>
            <div className="flex-between"><span className="text-secondary">Operation Costs</span><span className="mono">{fmt(data.opsTotal)}</span></div>
            <div style={{ borderTop: '1px solid var(--accent)', paddingTop: 8, marginTop: 4 }}>
              <div className="flex-between" style={{ marginBottom: 8 }}>
                <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 16 }}>TOTAL PRODUCT COST</span>
                <span className="cost-value mono" style={{ fontSize: 20, fontWeight: 700 }}>{fmt(data.grandTotal)}</span>
              </div>
              <div className="flex-between" style={{ alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Batch Size (kg)</span>
                <input type="number" value={batchSize} min="1" step="1"
                  onChange={e => setBatchSize(e.target.value)}
                  onBlur={e => { if (latestVersion) { import('axios').then(m => { const API = m.default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' }); API.put('/costsheet/versions/'+latestVersion.id+'/batch-size', { batch_size: parseFloat(e.target.value)||100 }).then(() => costSheetAPI.get(product.id).then(r => { setData(r.data); setBatchSize(r.data.batchSize || 100); })); }); } }}
                  style={{ width: 90, padding: '4px 8px', fontSize: 13, textAlign: 'right', border: '1px solid var(--accent)', borderRadius: 6 }} />
              </div>
              <div className="flex-between" style={{ background: 'var(--accent)', borderRadius: 8, padding: '10px 14px' }}>
                <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 15, color: 'white' }}>COST PER KG</span>
                <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 22, fontWeight: 700, color: 'white' }}>AED {batchSize > 0 ? (data.grandTotal / parseFloat(batchSize)).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Packing Modal */}
      {showAddPacking && (
        <div className="modal-overlay" onClick={() => setShowAddPacking(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Add Packing Material</div>
              <button className="modal-close" onClick={() => setShowAddPacking(false)}>×</button>
            </div>
            <div className="form-group" style={{ position: 'relative' }}>
              <label>Search Item (code or name)</label>
              <input placeholder="Type to search..." value={packingSearch}
                onChange={e => { setPackingSearch(e.target.value); setShowPackingDropdown(true); }}
                onFocus={() => setShowPackingDropdown(true)} />
              {showPackingDropdown && packingSearch.length > 1 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6, zIndex: 200, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
                  {itemMaster.filter(i => i.item_code?.toLowerCase().includes(packingSearch.toLowerCase()) || i.item_name?.toLowerCase().includes(packingSearch.toLowerCase())).slice(0,15).map(item => (
                    <div key={item.id} onClick={() => {
                      setPackingForm({ ...packingForm, item_code: item.item_code, item_name: item.item_name, unit: item.unit||'pcs', unit_cost: item.unit_cost||0 });
                      setPackingSearch(item.item_code + ' — ' + item.item_name);
                      setShowPackingDropdown(false);
                    }} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 12 }}
                    onMouseEnter={e => e.currentTarget.style.background='var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                      <span className="mono" style={{ color: 'var(--accent)', fontSize: 11 }}>{item.item_code}</span>
                      <span style={{ marginLeft: 8 }}>{item.item_name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="form-grid">
              <div className="form-group full">
                <label>Select from Item Master (optional)</label>
                <select value={packingForm.item_code} onChange={e => handleItemSelect(e.target.value)}>
                  <option value="">— Select item or enter manually below —</option>
                  {itemMaster.map(i => <option key={i.id} value={i.item_code}>{i.item_code} — {i.item_name}</option>)}
                </select>
              </div>
              <div className="form-group full">
                <label>Item Name *</label>
                <input placeholder="e.g. Carton Box 500g" value={packingForm.item_name} onChange={e => setPackingForm({ ...packingForm, item_name: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Quantity</label>
                <input type="number" step="0.001" value={packingForm.quantity} onChange={e => setPackingForm({ ...packingForm, quantity: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Unit</label>
                <input placeholder="pcs, kg, m" value={packingForm.unit} onChange={e => setPackingForm({ ...packingForm, unit: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Unit Cost (AED)</label>
                <input type="number" step="0.01" value={packingForm.unit_cost} onChange={e => setPackingForm({ ...packingForm, unit_cost: e.target.value })} />
              </div>
              {packingForm.quantity && packingForm.unit_cost && (
                <div className="form-group">
                  <label>Total</label>
                  <div style={{ padding: '9px 12px', background: 'var(--accent-dim)', borderRadius: 6, fontFamily: 'DM Mono, monospace', fontSize: 13, color: 'var(--accent)' }}>
                    AED {(parseFloat(packingForm.quantity) * parseFloat(packingForm.unit_cost)).toLocaleString('en-AE', { maximumFractionDigits: 2 })}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddPacking(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addPacking}>Add</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Operation Modal */}
      {showAddOp && (
        <div className="modal-overlay" onClick={() => setShowAddOp(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Add Operation Cost</div>
              <button className="modal-close" onClick={() => setShowAddOp(false)}>×</button>
            </div>
            <div className="form-grid">
              <div className="form-group full">
                <label>Description *</label>
                <input placeholder="e.g. Labour, Energy, Overheads" value={opForm.description} onChange={e => setOpForm({ ...opForm, description: e.target.value })} />
              </div>
              <div className="form-group full">
                <label>Amount (AED) *</label>
                <input type="number" step="0.01" placeholder="0.00" value={opForm.amount} onChange={e => setOpForm({ ...opForm, amount: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddOp(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addOperation}>Add</button>
            </div>
          </div>
        </div>
      )}
      {showSfgOpsInfo && (
        <div className="modal-overlay" onClick={() => setShowSfgOpsInfo(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <div className="modal-title">SFG Operation Costs Breakdown</div>
              <button className="modal-close" onClick={() => setShowSfgOpsInfo(false)}>✕</button>
            </div>
            <div style={{ padding: '4px 0 12px' }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                Operation costs from linked SFG products, weighted by batch percentage.
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-elevated)' }}>
                    <th style={{ padding: '7px 10px', fontSize: 11, textAlign: 'left' }}>Operation</th>
                    <th style={{ padding: '7px 10px', fontSize: 11, textAlign: 'left' }}>Source</th>
                    <th style={{ padding: '7px 10px', fontSize: 11, textAlign: 'right' }}>Amount (AED)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.operations.filter(o => String(o.id).startsWith('sfg-op-')).map((o, idx) => {
                    const parts = o.description.split(' (via ');
                    const opName = parts[0];
                    const source = parts[1] ? parts[1].replace(')', '') : '';
                    return (
                      <tr key={idx} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '7px 10px', fontSize: 12 }}>{opName}</td>
                        <td style={{ padding: '7px 10px', fontSize: 11, color: '#E8541A' }}>{source}</td>
                        <td style={{ padding: '7px 10px', fontSize: 12, textAlign: 'right', fontWeight: 600 }}>AED {(parseFloat(o.amount)||0).toFixed(3)}</td>
                      </tr>
                    );
                  })}
                  <tr style={{ borderTop: '2px solid var(--accent)', background: 'var(--bg-elevated)' }}>
                    <td colSpan={2} style={{ padding: '8px 10px', fontWeight: 700, fontSize: 12 }}>Total SFG Operations</td>
                    <td style={{ padding: '8px 10px', fontWeight: 700, fontSize: 13, textAlign: 'right', color: 'var(--accent)' }}>
                      AED {data.operations.filter(o => String(o.id).startsWith('sfg-op-')).reduce((s,o) => s + (parseFloat(o.amount)||0), 0).toFixed(3)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setShowSfgOpsInfo(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    {showPackingTemplate && <TemplateSearchModal type="packing" onSelect={loadPackingTemplate} onClose={() => setShowPackingTemplate(false)} />}
    {showOpTemplate && <TemplateSearchModal type="operation" onSelect={loadOpTemplate} onClose={() => setShowOpTemplate(false)} />}
    </div>
  );
}
